export const STUDENT_INFO_DIRECT_FIELD_KEYS = [
  "ex_one",
  "ex_two",
  "ex_three",
  "ex_four",
  "ex_five",
];

/** 추가 JSON 필드 값은 studentinfos.custom_data JSON 컬럼에 객체로 저장 */
export const STUDENT_INFO_JSON_BAG_KEY = "custom_data";

export const STUDENT_INFO_FIELD_TYPES = [
  { value: "text", label: "텍스트" },
  { value: "textarea", label: "긴 글" },
  { value: "date", label: "날짜" },
  { value: "select", label: "선택" },
];

export const DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS = {
  version: 1,
  fields: STUDENT_INFO_DIRECT_FIELD_KEYS.map((key, index) => ({
    key,
    label: "",
    enabled: false,
    type: "text",
    options: [],
    showInTable: true,
    showInModal: true,
    order: index + 1,
  })),
  jsonFields: [],
};

export function normalizeCustomFieldId(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]/g, "");
}

export function parseSelectOptions(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);

  const raw = String(value || "").trim();
  if (!raw) return [];

  const parts = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];

    if (inQuotes) {
      if (c === '"') {
        if (raw[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      const t = cur.trim();
      if (t) parts.push(t);
      cur = "";
    } else {
      cur += c;
    }
  }

  const t = cur.trim();
  if (t) parts.push(t);

  return parts;
}

function escapeOptionForCommaInput(option) {
  const s = String(option);
  if (/[",]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** 설정 화면 입력용: 쉼표 구분 문자열 (값 안에 쉼표가 있으면 따옴표로 이스케이프) */
export function selectOptionsToCommaInput(options) {
  const arr = Array.isArray(options)
    ? options.map(String).filter(Boolean)
    : parseSelectOptions(options);
  return arr.map(escapeOptionForCommaInput).join(", ");
}

export function parseJsonBag(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (error) {
    return {};
  }
}

export function stringifyJsonBag(value) {
  const safeValue = value && typeof value === "object" ? value : {};
  const compactValue = Object.fromEntries(
    Object.entries(safeValue).filter(([, item]) => item !== "" && item != null),
  );

  return compactValue;
}

export function normalizeStudentInfoCustomSettings(dataValue) {
  const source =
    dataValue && typeof dataValue === "object"
      ? dataValue
      : DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS;
  const incomingFields = Array.isArray(source.fields) ? source.fields : [];
  const incomingJsonFields = Array.isArray(source.jsonFields)
    ? source.jsonFields
    : [];

  const fields = DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS.fields.map(
    (defaultField) => {
      const savedField = incomingFields.find(
        (field) => field?.key === defaultField.key,
      );

      return {
        ...defaultField,
        ...savedField,
        key: defaultField.key,
        label: savedField?.label || "",
        type: savedField?.type || "text",
        options: parseSelectOptions(savedField?.options),
        enabled: Boolean(savedField?.enabled),
        showInTable: savedField?.showInTable !== false,
        showInModal: savedField?.showInModal !== false,
        order: Number(savedField?.order || defaultField.order),
      };
    },
  );

  const jsonFields = incomingJsonFields
    .map((field, index) => ({
      id: normalizeCustomFieldId(field?.id),
      label: field?.label || "",
      enabled: Boolean(field?.enabled),
      type: field?.type || "text",
      options: parseSelectOptions(field?.options),
      showInTable: field?.showInTable !== false,
      showInModal: field?.showInModal !== false,
      order: Number(field?.order || fields.length + index + 1),
    }))
    .filter((field) => field.id);

  return {
    version: 1,
    fields,
    jsonFields,
  };
}

export function getActiveCustomFields(settings, area = "table") {
  const normalized = normalizeStudentInfoCustomSettings(settings);
  const visibleKey = area === "modal" ? "showInModal" : "showInTable";

  return [
    ...normalized.fields
      .filter((field) => field.enabled && field[visibleKey])
      .map((field) => ({ ...field, storage: "direct" })),
    ...normalized.jsonFields
      .filter((field) => field.enabled && field[visibleKey])
      .map((field) => ({ ...field, key: field.id, storage: "json" })),
  ].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

export function getCustomFieldValue(studentInfoRow, field) {
  if (!studentInfoRow || !field) return "";

  if (field.storage === "json") {
    return parseJsonBag(studentInfoRow[STUDENT_INFO_JSON_BAG_KEY])[field.id] || "";
  }

  return studentInfoRow[field.key] || "";
}
